/**
 * ScrollManager
 * Handles scroll container dimensions, visible range calculations, and scroll events
 */
class ScrollManager {
  constructor(options = {}) {
    // Use centralized config (with fallbacks)
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};

    // Configuration
    this.containerSelector = options.containerSelector || "#product-grid";
    this.itemHeight = options.itemHeight || cfg.ITEM_HEIGHT_DESKTOP || 320;
    this.columnsDesktop = options.columnsDesktop || cfg.COLUMNS_DESKTOP || 4;
    this.columnsMobile = options.columnsMobile || cfg.COLUMNS_MOBILE || 2;
    this.throttleDelay = options.throttleDelay || cfg.SCROLL_THROTTLE_DELAY || 16; // ~60fps
    this.mobileBreakpoint = cfg.MOBILE_BREAKPOINT || 750;

    // State
    this.scrollContainer = null;
    this.scrollTop = 0;
    this.viewportHeight = 0;
    this.totalItems = 0;
    this.loadedCount = 0; // Track how many products are actually loaded
    this.rowHeight = this.itemHeight;
    this.isInitialized = false;
    this.gridOffsetTop = options.gridOffsetTop || 0; // Grid position from document start

    // Callbacks
    this.onVisibleRangeChange = options.onVisibleRangeChange || null;
    this.onScrollEnd = options.onScrollEnd || null;
    this.onBoundaryReached = options.onBoundaryReached || null;

    // Throttle state
    this.lastScrollTime = 0;
    this.scrollEndTimeout = null;
    this.scrollEndDelay = cfg.SCROLL_END_DELAY || 150;

    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  /**
   * Initialize the scroll manager
   * @param {number} totalItems - Total number of items to display
   */
  initialize(totalItems) {
    this.scrollContainer = document.querySelector(this.containerSelector);
    if (!this.scrollContainer) {
      return false;
    }

    this.totalItems = totalItems;
    this.updateDimensions();
    this.setupScrollContainer();
    this.setupEventListeners();
    this.isInitialized = true;
    return true;
  }

  /**
   * Setup scroll container as the scrollable viewport
   */
  setupScrollContainer() {
    this.scrollContainer.style.position = "relative";
    this.scrollContainer.style.width = "100%";
    this.scrollContainer.style.overflowY = "visible";
    this.scrollContainer.style.overflowX = "hidden";
    this.updateContainerHeight();
  }

  /**
   * Update container height based on loaded items
   */
  updateContainerHeight() {
    if (!this.scrollContainer) return;

    // Use loadedCount if available, otherwise fall back to totalItems
    const itemCount = this.loadedCount > 0 ? this.loadedCount : this.totalItems;
    if (!itemCount) return;

    const columns = this.getColumns();
    const totalRows = Math.ceil(itemCount / columns);
    const totalHeight = totalRows * this.rowHeight;

    this.scrollContainer.style.height = `${totalHeight}px`;
  }

  /**
   * Get current column count based on viewport
   * @returns {number}
   */
  getColumns() {
    // Use mobileBreakpoint from config
    return window.innerWidth >= this.mobileBreakpoint ? this.columnsDesktop : this.columnsMobile;
  }

  /**
   * Update viewport dimensions (use window height)
   */
  updateDimensions() {
    // Use window height as viewport (page scrolls, not container)
    this.viewportHeight = window.innerHeight;

    // Get vertical gap from CSS variables to match container-pool positioning
    const computedStyle = getComputedStyle(document.documentElement);
    const vGap =
      window.innerWidth >= this.mobileBreakpoint
        ? parseFloat(
            computedStyle.getPropertyValue("--grid-desktop-vertical-spacing")
          ) || 16
        : parseFloat(
            computedStyle.getPropertyValue("--grid-mobile-vertical-spacing")
          ) || 8;

    // rowHeight must include gap to match container-pool positioning
    this.rowHeight = this.itemHeight + vGap;
    this.effectiveViewportHeight = this.viewportHeight;
  }

  /**
   * Setup scroll and resize event listeners
   */
  setupEventListeners() {
    // Listen to window scroll (container has overflow: visible)
    window.addEventListener("scroll", this.handleScroll, {
      passive: true,
    });
    window.addEventListener("resize", this.handleResize, { passive: true });
  }

  /**
   * Remove event listeners (cleanup)
   */
  destroy() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);

    if (this.scrollEndTimeout) {
      clearTimeout(this.scrollEndTimeout);
    }

    this.isInitialized = false;
  }

  /**
   * Handle scroll events with throttling
   */
  handleScroll() {
    const now = Date.now();

    // Throttle scroll handling
    if (now - this.lastScrollTime < this.throttleDelay) {
      return;
    }
    this.lastScrollTime = now;

    // Calculate scroll position relative to container
    const containerRect = this.scrollContainer.getBoundingClientRect();
    // relativeScrollTop = how far the container top has scrolled past viewport top
    // When container top is at viewport top, relativeScrollTop = 0
    // When container has scrolled up, relativeScrollTop = positive value
    const relativeScrollTop = Math.max(0, -containerRect.top);
    this.scrollTop = relativeScrollTop;

    // Calculate visible range
    const visibleRange = this.calculateVisibleRange(relativeScrollTop);

    // Notify listeners
    if (this.onVisibleRangeChange) {
      this.onVisibleRangeChange(visibleRange);
    }

    // Check boundary based on visible product indices
    this.checkBoundary(visibleRange);

    // Handle scroll end detection
    this.detectScrollEnd();
  }

  /**
   * Detect when scrolling has stopped
   */
  detectScrollEnd() {
    if (this.scrollEndTimeout) {
      clearTimeout(this.scrollEndTimeout);
    }

    this.scrollEndTimeout = setTimeout(() => {
      if (this.onScrollEnd) {
        this.onScrollEnd(this.scrollTop);
      }
    }, this.scrollEndDelay);
  }

  /**
   * Handle window resize
   */
  handleResize() {
    this.updateDimensions();
    this.updateContainerHeight();

    // Recalculate visible range after resize
    this.handleScroll();
  }

  /**
   * Calculate visible item range based on scroll position
   * @param {number} relativeScrollTop - Scroll position relative to container
   * @returns {Object} { start, end, visibleStart, visibleEnd }
   */
  calculateVisibleRange(relativeScrollTop) {
    const columns = this.getColumns();

    // Calculate first visible row
    const firstVisibleRow = Math.floor(relativeScrollTop / this.rowHeight);
    const firstVisibleIndex = firstVisibleRow * columns;

    // Calculate visible rows count
    const visibleRows =
      Math.ceil(this.effectiveViewportHeight / this.rowHeight) + 1;
    const visibleCount = visibleRows * columns;

    // Calculate last visible index (capped to loaded count, not total items)
    const maxIndex =
      this.loadedCount > 0 ? this.loadedCount - 1 : this.totalItems - 1;
    const lastVisibleIndex = Math.min(
      firstVisibleIndex + visibleCount - 1,
      maxIndex
    );

    return {
      start: Math.max(0, firstVisibleIndex),
      end: lastVisibleIndex,
      visibleStart: firstVisibleIndex,
      visibleEnd: lastVisibleIndex,
      firstRow: firstVisibleRow,
      visibleRows: visibleRows,
    };
  }

  /**
   * Calculate buffer range (containers to keep rendered but off-screen)
   * @param {Object} visibleRange - Current visible range
   * @param {number} containerCount - Total available containers
   * @returns {Object} { start, end }
   */
  calculateBufferRange(visibleRange, containerCount) {
    const columns = this.getColumns();
    const visibleCount = visibleRange.end - visibleRange.start + 1;

    // Calculate available buffer slots
    const bufferSlots = containerCount - visibleCount;
    const bufferRows = Math.floor(bufferSlots / columns / 2);
    const bufferItems = bufferRows * columns;

    // Distribute buffer evenly above and below visible range (capped to loaded count)
    const maxIndex =
      this.loadedCount > 0 ? this.loadedCount - 1 : this.totalItems - 1;
    const bufferStart = Math.max(0, visibleRange.start - bufferItems);
    const bufferEnd = Math.min(maxIndex, visibleRange.end + bufferItems);

    return {
      start: bufferStart,
      end: bufferEnd,
    };
  }

  /**
   * Check if scroll has reached data boundary based on visible product indices
   * @param {Object} visibleRange - { start, end } indices of visible products
   */
  checkBoundary(visibleRange) {
    if (!this.onBoundaryReached) return;

    const columns = this.getColumns();
    const thresholdRows = 2; // Trigger 2 rows before end
    const thresholdItems = thresholdRows * columns;

    // Check if approaching end of loaded content
    // Only trigger if there are more items to load
    if (this.loadedCount < this.totalItems && this.loadedCount > 0) {
      const triggerPoint = this.loadedCount - thresholdItems;
      if (visibleRange.end >= triggerPoint) {
        this.onBoundaryReached("bottom", {
          visibleEnd: visibleRange.end,
          loadedCount: this.loadedCount,
          totalItems: this.totalItems,
        });
      }
    }

    // Check if near top (for bidirectional loading if needed)
    if (visibleRange.start <= thresholdItems) {
      this.onBoundaryReached("top", {
        visibleStart: visibleRange.start,
        loadedCount: this.loadedCount,
      });
    }
  }

  /**
   * Scroll to a specific item index
   * @param {number} index - Item index to scroll to
   * @param {string} [behavior='instant'] - 'instant' or 'smooth'
   */
  scrollToIndex(index, behavior = "instant") {
    const columns = this.getColumns();
    const row = Math.floor(index / columns);
    const targetScrollTop = row * this.rowHeight;

    // Get container's position relative to document
    const containerRect = this.scrollContainer.getBoundingClientRect();
    const containerTop = containerRect.top + window.scrollY;

    // Scroll window to position container content at target
    window.scrollTo({
      top: containerTop + targetScrollTop,
      behavior: behavior,
    });
  }

  /**
   * Get the item index at current scroll position
   * @returns {number}
   */
  getCurrentIndex() {
    const columns = this.getColumns();
    const row = Math.floor(this.scrollTop / this.rowHeight);
    return row * columns;
  }

  /**
   * Get current grid height in pixels
   * @returns {number}
   */
  getGridHeight() {
    if (!this.scrollContainer) return 0;
    
    // Calculate from loaded items
    const columns = this.getColumns();
    const totalRows = Math.ceil(this.loadedCount / columns);
    const calculatedHeight = totalRows * this.rowHeight;
    
    // Verify with actual measured height
    const measuredHeight = this.scrollContainer.offsetHeight || this.scrollContainer.getBoundingClientRect().height;
    
    // Return calculated height (more reliable for virtual scrolling)
    return calculatedHeight || measuredHeight || 0;
  }

  /**
   * Calculate position for a specific item index
   * @param {number} index - Item index
   * @returns {Object} { top, left, row, column }
   */
  getItemPosition(index) {
    const columns = this.getColumns();
    const row = Math.floor(index / columns);
    const column = index % columns;

    const top = row * this.rowHeight;
    // Left position will be handled by CSS grid or calculated based on container width
    const containerWidth = this.scrollContainer.offsetWidth;
    const itemWidth = containerWidth / columns;
    const left = column * itemWidth;

    return {
      top,
      left,
      row,
      column,
      width: itemWidth,
      height: this.rowHeight,
    };
  }

  /**
   * Update total items count (total in collection)
   * @param {number} newTotal
   */
  setTotalItems(newTotal) {
    this.totalItems = newTotal;
    this.updateContainerHeight();
  }

  /**
   * Update loaded count (how many products are actually loaded)
   * @param {number} count
   */
  setLoadedCount(count) {
    this.loadedCount = count;
    this.updateContainerHeight(); // Update height to match loaded products
  }

  /**
   * Get current scroll state
   * @returns {Object}
   */
  getState() {
    return {
      scrollTop: this.scrollTop,
      viewportHeight: this.viewportHeight,
      effectiveViewportHeight: this.effectiveViewportHeight,
      totalItems: this.totalItems,
      loadedCount: this.loadedCount,
      columns: this.getColumns(),
      rowHeight: this.rowHeight,
      currentIndex: this.getCurrentIndex(),
      containerHeight: this.scrollContainer?.offsetHeight || 0,
    };
  }
}

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = ScrollManager;
}
